import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { siteConfigService } from '../services/api';
import { updateMetaTags } from '../utils/seo';
import { Facebook, Instagram, Linkedin, Youtube, Mail, Phone, ShoppingCart, LayoutDashboard, User, LogOut, Search, ChevronDown, Menu, X, MapPin } from 'lucide-react';
import XLogo from './XLogo';

function Header({ onOpenSearch, onOpenCart }) {
  const { isAuthenticated, logout, user } = useAuth();
  const isAdmin = user?.roles?.some((role) => ['admin', 'super_admin'].includes(role.slug));
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [siteName, setSiteName] = useState('Ortus');
  const [siteLogo, setSiteLogo] = useState('');
  const [settings, setSettings] = useState({});
  const [hoveredItem, setHoveredItem] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setHoveredItem(null);
  }, [location.pathname]);

  const loadConfig = async () => {
    try {
      const [menu, settingsData] = await Promise.all([
        siteConfigService.menu('header'),
        siteConfigService.settings(),
      ]);
      setMenuItems(Array.isArray(menu) ? menu : menu?.data || []);
      setSettings(settingsData?.data || settingsData || {});
      setSiteName(settingsData?.site_name || settingsData?.data?.site_name || 'Ortus');
      setSiteLogo(settingsData?.site_logo || settingsData?.data?.site_logo || '');
      
      updateMetaTags({
        favicon: settingsData?.site_favicon || settingsData?.data?.site_favicon,
        siteName: settingsData?.site_name || settingsData?.data?.site_name || 'Ortus'
      });
    } catch (e) {
      console.error('Header config load error:', e);
    }
  };

  const socialIcons = [
    { key: 'social_facebook', icon: Facebook, label: 'Facebook' },
    { key: 'social_twitter', icon: XLogo, label: 'X (Twitter)' },
    { key: 'social_instagram', icon: Instagram, label: 'Instagram' },
    { key: 'social_linkedin', icon: Linkedin, label: 'LinkedIn' },
    { key: 'social_youtube', icon: Youtube, label: 'YouTube' },
  ];

  const topItems = menuItems.filter(i => !i.parent_id);
  const getChildren = (parentId) => menuItems.filter(i => i.parent_id === parentId);

  const isActive = (url) => location.pathname === url || (url !== '/' && location.pathname.startsWith(url));

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenSearch?.();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onOpenSearch]);

  return (
    <header className="sticky top-0 z-50">
      {/* Top utility bar */}
      <div className="bg-slate-950 text-slate-300 text-xs hidden md:block relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-red-600/10 via-transparent to-red-600/10"></div>
        <div className="px-4 lg:px-8 xl:px-12 h-12 flex items-center relative">
          {/* Left: Contact info */}
          <div className="flex items-center gap-4 flex-shrink-0">
            {settings?.contact_phone && (
              <a href={`tel:${settings.contact_phone}`} className="flex items-center gap-1.5 hover:text-red-400 transition-colors duration-200">
                <Phone size={12} strokeWidth={2.5} />
                <span className="font-medium">{settings.contact_phone}</span>
              </a>
            )}
            {settings?.contact_email && (
              <a href={`mailto:${settings.contact_email}`} className="flex items-center gap-1.5 hover:text-red-400 transition-colors duration-200">
                <Mail size={12} strokeWidth={2.5} />
                <span className="font-medium">{settings.contact_email}</span>
              </a>
            )}
          </div>
          {/* Center: Scrolling marquee text */}
          <div className="flex-1 mx-6 overflow-hidden h-12 flex items-center">
            <div className="marquee-track">
              <span className="text-red-500 font-bold tracking-wide whitespace-nowrap italic" style={{ fontFamily: "'Edwardian Script ITC', 'Kunstler Script', 'Bickham Script Pro', 'Great Vibes', cursive", fontSize: '22px' }}>
                Crafting Lives, Delivering Trust
              </span>
            </div>
          </div>
          {/* Right: Social icons + Contact link */}
          <div className="flex items-center gap-5 flex-shrink-0">
            <div className="flex items-center gap-3 pr-5 border-r border-slate-800">
              {socialIcons.map((social) => {
                const url = settings[social.key];
                if (!url) return null;
                const Icon = social.icon;
                return (
                  <a
                    key={social.key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-500 hover:text-red-400 transition-all duration-200 hover:scale-125"
                    title={social.label}
                  >
                    <Icon size={14} strokeWidth={2.5} />
                  </a>
                );
              })}
            </div>
            <div className="flex items-center gap-4 font-semibold">
              <Link to="/contact" className="hover:text-red-400 transition-colors text-[11px]">Contact</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className={`bg-white transition-all duration-300 ${scrolled ? 'shadow-xl shadow-slate-900/5' : 'shadow-md shadow-slate-900/5'}`}>
        {/* Accent line */}
        <div className="h-0.5 bg-gradient-to-r from-red-600 via-red-500 to-red-700"></div>
        
        <div className="px-4 lg:px-8 xl:px-12">
          {/* Row 1: Logo + Actions (always visible) */}
          <div className={`flex justify-between items-center transition-all duration-300 ${scrolled ? 'h-14 md:h-16' : 'h-16 md:h-20 2xl:h-24'}`}>
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              {siteLogo ? (
                <img src={siteLogo} alt={siteName} className={`w-auto object-contain transition-all duration-300 ${scrolled ? 'h-10 md:h-12 max-w-[140px]' : 'h-14 w-auto max-w-[120px] md:h-16 md:max-w-[160px] lg:h-20 lg:max-w-[200px]'}`} />
              ) : (
                <>
                  <span className="h-11 w-11 rounded-xl bg-gradient-to-br from-red-500 to-red-700 text-white flex items-center justify-center font-black text-xl shadow-lg shadow-red-600/30 group-hover:scale-110 transition-transform duration-300">
                    {siteName.charAt(0)}
                  </span>
                  <span>
                    <span className="block text-2xl font-black text-slate-900 tracking-tight leading-none">{siteName}</span>
                    <span className="block text-[11px] uppercase tracking-[0.28em] text-red-600 font-semibold mt-1">FMCG Limited</span>
                  </span>
                </>
              )}
            </Link>

            {/* Desktop nav - inline only on 2xl+ (1536px+), otherwise second row */}
            <nav className="hidden 2xl:flex items-center gap-0.5 flex-1 justify-center px-2">
              {topItems.map((item) => {
                const children = getChildren(item.id);
                const hasChildren = children.length > 0;
                const active = isActive(item.url);

                return (
                  <div
                    key={item.id}
                    className="relative"
                    onMouseEnter={() => setHoveredItem(item.id)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <Link
                      to={item.url}
                      className={`px-2.5 2xl:px-4 py-2 text-xs 2xl:text-sm font-semibold inline-flex items-center gap-1 rounded-lg transition-all duration-200 ${active ? 'text-red-700 bg-red-50' : 'text-slate-700 hover:text-red-700 hover:bg-red-50/50'}`}
                    >
                      {item.label}
                      {hasChildren && (
                        <ChevronDown size={14} className={`transition-transform duration-200 ${hoveredItem === item.id ? 'rotate-180' : ''}`} />
                      )}
                    </Link>
                    {hasChildren && hoveredItem === item.id && (
                      <div className="absolute top-full left-0 pt-2 w-64">
                        <div className="bg-white shadow-2xl shadow-slate-900/10 border border-slate-100 rounded-2xl py-2 overflow-hidden">
                          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 to-red-700 rounded-t-2xl"></div>
                          {children.map(child => (
                            <Link key={child.id} to={child.url} target={child.open_new_tab ? '_blank' : '_self'} className="flex items-center gap-3 px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-red-50 hover:text-red-700 transition-all duration-200 hover:pl-6">
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-300 group-hover:bg-red-500"></span>
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            {/* Right actions */}
            <div className="flex items-center gap-2">
              {/* Search */}
              <button
                type="button"
                onClick={() => onOpenSearch?.()}
                className="hidden md:flex items-center gap-2 px-3 lg:px-3.5 py-2 rounded-full border border-slate-200 text-xs font-semibold text-slate-500 hover:border-red-400 hover:text-red-600 transition-all duration-200"
              >
                <Search size={14} />
                <span className="hidden 2xl:inline">Search</span>
                <kbd className="hidden 2xl:inline bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded text-[10px] font-mono">⌘K</kbd>
              </button>

              {/* Cart */}
              <button
                type="button"
                onClick={() => onOpenCart?.()}
                className="p-2 2xl:p-2.5 rounded-full border border-slate-200 text-slate-600 hover:border-red-400 hover:text-red-600 hover:bg-red-50 transition-all duration-200 relative group"
              >
                <ShoppingCart size={18} />
              </button>

              {/* Auth */}
              {isAuthenticated ? (
                <div className="relative ml-1 group">
                  <div className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 transition-all duration-200 group-hover:bg-slate-100">
                    <span className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-red-700 text-white flex items-center justify-center text-xs font-black shadow-md">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                    <span className="hidden 2xl:inline">{user?.name || 'Account'}</span>
                    <span className="2xl:hidden">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
                    <ChevronDown size={14} className="text-slate-400 group-hover:rotate-180 transition-transform duration-200" />
                  </div>
                  <div className="absolute top-full right-0 pt-2 w-56 hidden group-hover:block">
                    <div className="bg-white shadow-2xl shadow-slate-900/10 border border-slate-100 rounded-2xl py-2 overflow-hidden">
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 to-red-700 rounded-t-2xl"></div>
                      <div className="px-5 py-3 border-b border-slate-100">
                        <p className="text-xs text-slate-400 font-medium">Signed in as</p>
                        <p className="text-sm font-bold text-slate-900 truncate">{user?.name}</p>
                        <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                      </div>
                      <Link to="/profile" className="flex items-center gap-3 px-5 py-2.5 text-sm text-slate-700 hover:bg-red-50 hover:text-red-700 transition-all duration-200 hover:pl-6">
                        <User size={15} className="text-slate-400" /> Profile
                      </Link>
                      {isAdmin && (
                        <Link to="/admin" className="flex items-center gap-3 px-5 py-2.5 text-sm text-slate-700 hover:bg-red-50 hover:text-red-700 transition-all duration-200 hover:pl-6">
                          <LayoutDashboard size={15} className="text-slate-400" /> Admin Dashboard
                        </Link>
                      )}
                      <div className="my-1 border-t border-slate-100"></div>
                      <button onClick={logout} className="flex items-center gap-3 w-full text-left px-5 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-all duration-200 hover:pl-6">
                        <LogOut size={15} /> Logout
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <Link to="/login" className="ml-1 bg-gradient-to-r from-red-600 to-red-700 text-white px-3 2xl:px-5 py-2 2xl:py-2.5 rounded-full text-xs 2xl:text-sm font-bold hover:shadow-lg hover:shadow-red-600/30 transition-all duration-200 hover:scale-105 whitespace-nowrap">
                  <span className="hidden 2xl:inline">Login / Register</span>
                  <span className="2xl:hidden">Login</span>
                </Link>
              )}

              {/* Mobile menu toggle */}
              <button className="md:hidden p-2.5 rounded-xl hover:bg-slate-100 transition" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? <X size={22} className="text-slate-700" /> : <Menu size={22} className="text-slate-700" />}
              </button>
            </div>
          </div>

          {/* Row 2: Nav menu for md to 2xl screens (768px-1535px) - wraps to second line */}
          <nav className="hidden md:flex 2xl:hidden items-center gap-0.5 flex-wrap justify-center pb-2 border-t border-slate-100 pt-2">
            {topItems.map((item) => {
              const children = getChildren(item.id);
              const hasChildren = children.length > 0;
              const active = isActive(item.url);

              return (
                <div
                  key={item.id}
                  className="relative"
                  onMouseEnter={() => setHoveredItem(item.id)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <Link
                    to={item.url}
                    className={`px-2.5 py-1.5 text-xs font-semibold inline-flex items-center gap-1 rounded-lg transition-all duration-200 ${active ? 'text-red-700 bg-red-50' : 'text-slate-700 hover:text-red-700 hover:bg-red-50/50'}`}
                  >
                    {item.label}
                    {hasChildren && (
                      <ChevronDown size={12} className={`transition-transform duration-200 ${hoveredItem === item.id ? 'rotate-180' : ''}`} />
                    )}
                  </Link>
                  {hasChildren && hoveredItem === item.id && (
                    <div className="absolute top-full left-0 pt-2 w-56 z-50">
                      <div className="bg-white shadow-2xl shadow-slate-900/10 border border-slate-100 rounded-2xl py-2 overflow-hidden">
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 to-red-700 rounded-t-2xl"></div>
                        {children.map(child => (
                          <Link key={child.id} to={child.url} target={child.open_new_tab ? '_blank' : '_self'} className="flex items-center gap-3 px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-red-50 hover:text-red-700 transition-all duration-200 hover:pl-6">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-300 group-hover:bg-red-500"></span>
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-100 bg-white max-h-[75vh] overflow-y-auto">
            <div className="px-4 py-4 space-y-1">
              {/* Mobile search */}
              <button
                onClick={() => { onOpenSearch?.(); setMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 text-slate-500 text-sm font-medium hover:bg-slate-100 transition mb-3"
              >
                <Search size={16} /> Search products...
              </button>

              {topItems.map((item) => {
                const children = getChildren(item.id);
                const active = isActive(item.url);
                return (
                  <div key={item.id}>
                    <Link
                      to={item.url}
                      className={`block px-4 py-3 rounded-xl text-sm font-bold transition ${active ? 'text-red-700 bg-red-50' : 'text-slate-800 hover:bg-slate-50'}`}
                    >
                      {item.label}
                    </Link>
                    {children.length > 0 && (
                      <div className="ml-4 border-l-2 border-red-100 space-y-0.5 my-1">
                        {children.map((child) => (
                          <Link key={child.id} to={child.url} className="block pl-5 pr-4 py-2 text-sm text-slate-600 hover:text-red-700 hover:bg-red-50/50 rounded-r-xl transition">
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Mobile divider */}
              <div className="border-t border-slate-100 my-3"></div>

              {/* Mobile quick links */}
              <div className="grid grid-cols-3 gap-2">
                <Link to="/contact" className="text-center px-2 py-2.5 rounded-xl bg-slate-50 text-xs font-semibold text-slate-600 hover:bg-red-50 hover:text-red-700 transition">Contact</Link>
                <Link to="/dealers" className="text-center px-2 py-2.5 rounded-xl bg-slate-50 text-xs font-semibold text-slate-600 hover:bg-red-50 hover:text-red-700 transition">Dealers</Link>
                <Link to="/distributors" className="text-center px-2 py-2.5 rounded-xl bg-slate-50 text-xs font-semibold text-slate-600 hover:bg-red-50 hover:text-red-700 transition">Distributors</Link>
                <Link to="/channel" className="text-center px-2 py-2.5 rounded-xl bg-slate-50 text-xs font-semibold text-slate-600 hover:bg-red-50 hover:text-red-700 transition">Channel</Link>
              </div>

              {/* Mobile auth */}
              {isAuthenticated ? (
                <div className="border-t border-slate-100 pt-3 space-y-1">
                  <div className="flex items-center gap-3 px-4 py-2">
                    <span className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-red-700 text-white flex items-center justify-center text-sm font-black">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                    <div>
                      <p className="text-sm font-bold text-slate-900">{user?.name}</p>
                      <p className="text-xs text-slate-500">{user?.email}</p>
                    </div>
                  </div>
                  <Link to="/profile" className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-red-50 hover:text-red-700 rounded-xl transition">
                    <User size={16} /> Profile
                  </Link>
                  {isAdmin && (
                    <Link to="/admin" className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-red-50 hover:text-red-700 rounded-xl transition">
                      <LayoutDashboard size={16} /> Admin Dashboard
                    </Link>
                  )}
                  <button onClick={logout} className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl transition">
                    <LogOut size={16} /> Logout
                  </button>
                </div>
              ) : (
                <Link to="/login" className="block px-4 py-3 mt-2 bg-gradient-to-r from-red-600 to-red-700 text-white text-sm font-bold rounded-xl text-center hover:shadow-lg transition">
                  Login / Register
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

export default Header;
